import {
  Box,
  Button,
  createListCollection,
  HStack,
  Portal,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'

import { Radio, RadioGroup } from '@mangovoice/mango-ui'
import type { ToastLike } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../../api/hotelsApi'
import { useHotelsI18n } from '../../i18n'
import { useSetRoomResetSchedule } from '../../queries/hooks'

interface ResetScheduleFormProps {
  api: HotelsApi
  pbxId: number
  pbxUuid: string
  toast: ToastLike
  currentResetTime?: string | null
  canSave: boolean
}

/**
 * Daily room reset schedule configuration.
 * Matches legacy Settings page:
 *   - Radio: "Everyday @ [time dropdown]" vs "Manually"
 *   - Saves the PBX's `reset_schedule` via PATCH /hotels/v1/pbxes/{pbx_uuid}/
 */
export const ResetScheduleForm = ({
  api,
  pbxId,
  pbxUuid,
  toast,
  currentResetTime,
  canSave,
}: ResetScheduleFormProps) => {
  const { text, locale } = useHotelsI18n()
  const isManual = !currentResetTime
  const [mode, setMode] = useState<'auto' | 'manual'>(
    isManual ? 'manual' : 'auto',
  )
  const [selectedTime, setSelectedTime] = useState<string>(
    currentResetTime || '05:00:00',
  )

  const timeOptions = useMemo(() => {
    const options: Array<{ label: string; value: string }> = []
    const formatter = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
    })

    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
        const date = new Date()
        date.setHours(h, m, 0, 0)
        options.push({ label: formatter.format(date), value })
      }
    }

    return options
  }, [locale])

  const timeCollection = useMemo(
    () => createListCollection({ items: timeOptions }),
    [timeOptions],
  )

  const selectedTimeLabel = useMemo(
    () =>
      timeOptions.find((o) => o.value === selectedTime)?.label ?? selectedTime,
    [selectedTime, timeOptions],
  )

  const setSchedule = useSetRoomResetSchedule({
    api,
    options: {
      onSuccess: () => {
        toast.create({
          title:
            mode === 'manual'
              ? text(
                  'hotels.settings.manualResetEnabled',
                  'Room status will be reset manually',
                )
              : text(
                  'hotels.settings.dailyResetAt',
                  'Room status will reset daily at {{time}}',
                  {
                    time: selectedTimeLabel,
                  },
                ),
          type: 'success',
        })
      },
      onError: () => {
        toast.create({
          title: text(
            'hotels.settings.scheduleSaveFailed',
            'Failed to update reset schedule',
          ),
          type: 'error',
        })
      },
    },
  })

  const handleSave = useCallback(() => {
    if (!canSave) return
    setSchedule.mutate({
      pbx_uuid: pbxUuid,
      pbx_id: pbxId,
      xml_rebuilt_at: mode === 'manual' ? null : selectedTime,
    })
  }, [canSave, setSchedule, pbxUuid, pbxId, mode, selectedTime])

  const isDirty =
    (mode === 'manual' && !isManual) ||
    (mode === 'auto' && (isManual || selectedTime !== currentResetTime))

  return (
    <VStack align="stretch" gap={4}>
      <Text fontWeight="semibold">
        {text('hotels.settings.resetRoomsToDirty', 'Reset Rooms to Dirty')}
      </Text>
      <Box
        p={4}
        borderWidth={1}
        borderColor="border.default"
        borderRadius="lg"
        bg="bg.default"
      >
        <RadioGroup
          value={mode}
          onValueChange={(e) => setMode(e.value as 'auto' | 'manual')}
        >
          <VStack align="start" gap={3}>
            <HStack gap={3}>
              <Radio value="auto" disabled={!canSave}>
                <HStack gap={2}>
                  <Text fontSize="sm">
                    {text('hotels.settings.everyDayAt', 'Everyday @')}
                  </Text>
                  <Select.Root
                    collection={timeCollection}
                    size="sm"
                    width="150px"
                    disabled={!canSave}
                    value={[selectedTime]}
                    onValueChange={(e) => {
                      setSelectedTime(e.value[0] ?? '05:00:00')
                      setMode('auto')
                    }}
                  >
                    <Select.Trigger borderRadius="l2">
                      <Select.ValueText />
                    </Select.Trigger>
                    <Portal>
                      <Select.Positioner>
                        <Select.Content maxH="250px" overflowY="auto">
                          {timeCollection.items.map((opt) => (
                            <Select.Item key={opt.value} item={opt}>
                              {opt.label}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Portal>
                  </Select.Root>
                </HStack>
              </Radio>
            </HStack>

            <Radio value="manual" disabled={!canSave}>
              <Text fontSize="sm">
                {text('hotels.settings.manually', 'Manually')}
              </Text>
            </Radio>
          </VStack>
        </RadioGroup>

        {canSave && isDirty && (
          <Button
            size="sm"
            colorPalette="brand"
            borderRadius="l2"
            onClick={handleSave}
            loading={setSchedule.isPending}
            mt={4}
          >
            {text('hotels.settings.saveSchedule', 'Save')}
          </Button>
        )}
      </Box>
    </VStack>
  )
}
